import React from 'react';

import { tv } from '../../tv';
import { Text } from '../text';

/**
 * A monospace run inline with other text: an identifier, a DNS record value,
 * an endpoint path. The chip (mono face, quiet tinted background, small
 * radius) marks the run as literal, something to be read character by
 * character rather than as prose.
 *
 * `CodeBlock` is the block: its own container on the recessed surface, keeping
 * its line breaks, with a label above it, for a value a person reads whole
 * (a tool's input, a query, a log). No language or highlighting concern.
 * Multi-line content inside rendered markdown is still Prose's job, which
 * styles the pre element the renderer emits.
 *
 * There is no size prop. The chip reads 0.9em of the text around it (the
 * inbox markdown precedent), so it rides the enclosing step: sm text gets a
 * proportionally smaller chip than base text with no call site saying
 * anything. The em value is the named text-code utility, because the closed
 * type scale has no between-step token and arbitrary values are banned.
 */
export interface CodeProps {
  /** The literal run to display. */
  children?: React.ReactNode;
  id?: string;
}

const codeStyles = tv({
  base: 'ck:font-mono ck:text-code ck:bg-neutral-a-3 ck:rounded-2 ck:px-1',
});

export const Code = React.forwardRef<HTMLElement, CodeProps>(function Code(
  { children, ...props },
  ref,
) {
  return (
    <code {...props} ref={ref} className={codeStyles()} data-component="code">
      {children}
    </code>
  );
});

const blockStyles = tv({
  slots: {
    root: 'ck:flex ck:min-w-0 ck:flex-col ck:gap-1',
    pre: [
      'ck:m-0 ck:overflow-auto ck:rounded-2 ck:border ck:border-border-divider',
      'ck:surface-recessed ck:p-2 ck:font-mono ck:text-xs ck:text-text-muted',
      'ck:whitespace-pre-wrap ck:break-words',
    ],
  },
  variants: {
    /* A block that may run long scrolls inside a fixed height, so the page
     * keeps its shape; the region that asks for it names the step. */
    maxHeight: {
      none: {},
      glance: { pre: 'ck:max-h-glance' },
      reading: { pre: 'ck:max-h-reading' },
    },
    /* The block is the error itself: it reads in the error role. */
    tone: {
      default: {},
      error: { pre: 'ck:border-error-border ck:text-error-text' },
    },
  },
  defaultVariants: { maxHeight: 'none', tone: 'default' },
});

/**
 * A value formatted for reading: JSON with two-space indentation, except that
 * a string with line breaks opens as a triple-quoted block so its lines stay
 * lines. JSON's own escaping would fold a query or a document into one line
 * of backslashes, and that line is what a person is here to read.
 */
export const formatValue = (value: unknown, indent = 0): string => {
  const pad = '  '.repeat(indent);
  const padInner = '  '.repeat(indent + 1);
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (typeof value === 'string') {
    if (!value.includes('\n')) return JSON.stringify(value);
    return ['"""', ...value.split('\n').map((line) => padInner + line), `${pad}"""`].join('\n');
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const items = value.map((item) => padInner + formatValue(item, indent + 1));
    return ['[', items.join(',\n'), `${pad}]`].join('\n');
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return '{}';
    const items = entries.map(
      ([key, item]) => `${padInner}${JSON.stringify(key)}: ${formatValue(item, indent + 1)}`,
    );
    return ['{', items.join(',\n'), `${pad}}`].join('\n');
  }
  return JSON.stringify(value) ?? 'null';
};

export interface CodeBlockProps {
  /** Text keeps its breaks; anything else is shown as formatted JSON, its multi-line strings as triple-quoted blocks. */
  value: unknown;
  /** A small label above the block: what the value is. */
  label?: string;
  /** Scroll inside a named height instead of growing: a glance's worth or a reading's worth. */
  maxHeight?: 'none' | 'glance' | 'reading';
  /** The block is an error: it reads in the error role. */
  tone?: 'default' | 'error';
  id?: string;
}

export const CodeBlock = React.forwardRef<HTMLDivElement, CodeBlockProps>(function CodeBlock(
  { value, label, maxHeight = 'none', tone = 'default', ...props },
  ref,
) {
  const { root, pre } = blockStyles({ maxHeight, tone });
  const text = typeof value === 'string' ? value : formatValue(value);
  return (
    <div {...props} ref={ref} className={root()} data-component="code-block">
      {label ? (
        <Text size="xs" color="muted" weight="medium">
          {label}
        </Text>
      ) : null}
      <pre className={pre()}>{text}</pre>
    </div>
  );
});
